import React, { useState } from 'react';
import { ComposableMap, Geographies, Geography } from 'react-simple-maps';
import { useNavigate } from 'react-router-dom';
import { useFilter } from '../../context/FilterContext';
import { statesData } from '../../data/mockData';

const GEO_URL = '/india-states.topo.json';

const SHADES     = ['#DBEAFE', '#93C5FD', '#3B82F6', '#1D4ED8', '#1E3A8A'];
const NO_DATA    = '#CBD5E1';
const HOVER_MULT = 0.82;

function getShade(value: number | undefined, min: number, max: number): string {
  if (value === undefined) return NO_DATA;
  const norm = (value - min) / (max - min || 1);
  return SHADES[Math.min(4, Math.floor(norm * 5))];
}

const NAME_TO_ID: Record<string, string> = {
  'Gujarat':            'GJ',
  'Tamil Nadu':         'TN',
  'Rajasthan':          'RJ',
  'Maharashtra':        'MH',
  'Karnataka':          'KA',
  'Madhya Pradesh':     'MP',
  'Kerala':             'KL',
  'Uttar Pradesh':      'UP',
  'Andhra Pradesh':     'AP',
  'Telangana':          'TG',
  'Odisha':             'OD',
  'Jharkhand':          'JH',
  'Bihar':              'BR',
  'West Bengal':        'WB',
  'Punjab':             'PB',
  'Haryana':            'HR',
  'Himachal Pradesh':   'HP',
  'Uttarakhand':        'UT',
  'Chhattisgarh':       'CG',
  'NCT of Delhi':       'DL',
  'Goa':                'GA',
};

const CLICKABLE = new Set(['Gujarat', 'Tamil Nadu', 'Rajasthan', 'Maharashtra',
  'Karnataka', 'Madhya Pradesh', 'Kerala', 'Uttar Pradesh']);

interface IndiaMapProps {
  /** When provided, clicking a state calls this instead of navigating */
  onStateSelect?: (name: string | null) => void;
  /** State name to highlight with a distinct border */
  selectedState?: string | null;
}

export default function IndiaMap({ onStateSelect, selectedState }: IndiaMapProps = {}) {
  const navigate = useNavigate();
  const { setSelectedState } = useFilter();
  const [hoveredState, setHoveredState] = useState<string | null>(null);
  const [tooltip, setTooltip] = useState<{ x: number; y: number; text: string } | null>(null);

  const genByName: Record<string, number> = {};
  statesData.forEach(s => {
    const entry = Object.entries(NAME_TO_ID).find(([, id]) => id === s.id);
    if (entry) genByName[entry[0]] = s.generationGWh;
  });

  const values = Object.values(genByName);
  const minVal = Math.min(...values);
  const maxVal = Math.max(...values);

  const handleClick = (name: string) => {
    if (!CLICKABLE.has(name)) return;
    if (onStateSelect) {
      // Panel mode: toggle selection
      onStateSelect(selectedState === name ? null : name);
    } else {
      // Legacy: navigate to states page
      setSelectedState(name);
      navigate('/national/states');
    }
  };

  return (
    <div className="relative w-full h-full flex flex-col">
      <ComposableMap
        projection="geoMercator"
        projectionConfig={{ scale: 1050, center: [82.5, 23.5] }}
        style={{ width: '100%', height: '100%' }}
      >
        <Geographies geography={GEO_URL}>
          {({ geographies }) =>
            geographies.map(geo => {
              const name = geo.properties.name as string | null;
              if (!name) return null;

              const genVal   = genByName[name];
              const fill     = getShade(genVal, minVal, maxVal);
              const isHover  = hoveredState === name;
              const isSelected = selectedState === name;
              const canClick = CLICKABLE.has(name);

              return (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  fill={isHover ? adjustBrightness(fill, HOVER_MULT) : fill}
                  stroke={isSelected ? '#1D4ED8' : '#FFFFFF'}
                  strokeWidth={isSelected ? 2.5 : 0.6}
                  style={{
                    default:  { outline: 'none', cursor: canClick ? 'pointer' : 'default' },
                    hover:    { outline: 'none', cursor: canClick ? 'pointer' : 'default' },
                    pressed:  { outline: 'none' },
                  }}
                  onMouseEnter={e => {
                    setHoveredState(name);
                    const rect = (e.target as SVGPathElement).closest('svg')!.getBoundingClientRect();
                    setTooltip({
                      x: e.clientX - rect.left + 12,
                      y: e.clientY - rect.top  - 36,
                      text: genVal !== undefined
                        ? `${name}: ${genVal.toFixed(2)} GWh today`
                        : `${name}: No data`,
                    });
                  }}
                  onMouseMove={e => {
                    const rect = (e.target as SVGPathElement).closest('svg')!.getBoundingClientRect();
                    setTooltip(prev => prev
                      ? { ...prev, x: e.clientX - rect.left + 12, y: e.clientY - rect.top - 36 }
                      : null);
                  }}
                  onMouseLeave={() => { setHoveredState(null); setTooltip(null); }}
                  onClick={() => handleClick(name)}
                />
              );
            })
          }
        </Geographies>
      </ComposableMap>

      {tooltip && (
        <div
          className="absolute z-50 px-2.5 py-1.5 rounded text-white text-xs shadow-lg pointer-events-none"
          style={{ left: tooltip.x, top: tooltip.y, background: 'rgba(15,23,42,0.90)', whiteSpace: 'nowrap' }}
        >
          {tooltip.text}
        </div>
      )}

      <div className="flex items-center justify-between mt-2 px-1">
        <div className="flex items-center gap-2">
          <span className="text-xs text-text-tertiary">Low</span>
          <div className="flex gap-0.5">
            {SHADES.map((c, i) => (
              <div key={i} className="w-7 h-2.5 rounded-sm" style={{ backgroundColor: c }} />
            ))}
          </div>
          <span className="text-xs text-text-tertiary">High</span>
          <span className="text-xs text-text-tertiary ml-1">(GWh today)</span>
        </div>
        <span className="text-xs text-text-tertiary italic">
          {onStateSelect ? 'Click a state to view details' : 'Click a highlighted state to drill down'}
        </span>
      </div>
    </div>
  );
}

function adjustBrightness(hex: string, factor: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const c = (v: number) => Math.round(Math.min(255, v * factor)).toString(16).padStart(2, '0');
  return `#${c(r)}${c(g)}${c(b)}`;
}
